// Frontend types and presentation helpers for the per-academy product store.
//
// Catalog DATA now comes from the public API (GET /api/public/companies/:slug/
// store), served by the backend from the static mock manifest
// `config/store_catalogs.json` (see `StoreCatalogs`). This module keeps only the
// shared types and the pure grouping/teaser helpers that turn a fetched catalog
// into rendered sections. This is still a v1 that only exposes products (no
// cart, no checkout): selling happens in the WhatsApp conversation the store
// links to. Whether an academy has a store is signalled by the `has_store` flag
// on the company payload, not by a lookup here.

export type StoreCategory = 'kimonos' | 'rashguards' | 'shorts' | 'camisetas';

// One technical-spec row on the product detail page, e.g.
// { label: 'Composição', value: '100% algodão' }.
export interface StoreProductSpec {
  label: string;
  value: string;
}

export interface StoreProduct {
  id: string;
  name: string;
  category: StoreCategory;
  // Absolute path from the site root (served from `public/`).
  image: string;
  // Optional product variant note, e.g. "Manga curta/longa". Rendered as a
  // caption under the name.
  variant?: string | null;
  // Detail-page fields. Present in the catalog API; the grid only needs the
  // fields above, so these are optional for the listing's lighter shape.
  description?: string | null;
  sizes?: string[];
  specs?: StoreProductSpec[];
}

export interface StoreCatalog {
  products: StoreProduct[];
}

// Category render order and labels. Sections render in this order; categories
// with no products in a given catalog are skipped.
export const STORE_CATEGORIES: Array<{ id: StoreCategory; label: string }> = [
  { id: 'kimonos', label: 'Kimonos' },
  { id: 'rashguards', label: 'Rashguards' },
  { id: 'shorts', label: 'Shorts' },
  { id: 'camisetas', label: 'Camisetas' },
];

// Groups a catalog's products into the STORE_CATEGORIES order, dropping empty
// categories. Used by the store page to render one section per category.
export function storeProductsByCategory(
  catalog: StoreCatalog
): Array<{ id: StoreCategory; label: string; products: StoreProduct[] }> {
  return STORE_CATEGORIES.map((category) => ({
    ...category,
    products: catalog.products.filter((product) => product.category === category.id),
  })).filter((section) => section.products.length > 0);
}

// Picks up to `count` products for a teaser, preferring one per category (in
// STORE_CATEGORIES order) before taking a second from any category. Used by the
// store preview on the company page.
export function previewStoreProducts(catalog: StoreCatalog, count = 3): StoreProduct[] {
  const byCategory = storeProductsByCategory(catalog);
  const picked: StoreProduct[] = [];

  // First pass: one from each category, in order.
  for (const section of byCategory) {
    if (picked.length >= count) break;
    picked.push(section.products[0]);
  }
  // Second pass: fill remaining slots from leftovers, keeping category order.
  for (const section of byCategory) {
    for (const product of section.products.slice(1)) {
      if (picked.length >= count) break;
      picked.push(product);
    }
  }

  return picked.slice(0, count);
}
